import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { observer } from 'mobx-react'
import TransitionLayout from '@/ui/TransitionLayout'
import Heading from '@/ui/Heading'
import Progress from '@/ui/Progress'
import { Icon, Icons } from '@/ui/icon'
import FileUploader from '@/components/FileUploader'
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableCellActions,
  TableCellButton,
  TableSearch,
} from '@/ui/table'
import Chip from '@/ui/Chip'
import { useTableSearch, useTableSort } from '@/ui/table/hooks'
import rootStore from '../store'
import CantAccessKnowledgeBase from '@/components/CantAccessKnowledgeBase'
import {
  useKnowledgeBaseById,
  useKnowledgeBaseDocuments,
} from '@/features/knowledge-bases/hooks'
import LinearProgress from '@/ui/LinearProgress'
import { toDisplayDate } from '@/utils/dates'
import { checkRAGAccess } from '@/features/knowledge-bases/common'
import {
  bytesToGB,
  TOTAL_KB_STORAGE,
  TOTAL_DOCUMENTS_PER_PAGE,
} from '@/features/knowledge-bases/domain'
import {
  KnowledgeBaseModel,
  MASKED_GLOBAL_TOOLKIT_ID,
} from '@/features/knowledge-bases/api'

const getDocumentStatusProps = (
  indexerStatus: 'active' | 'idle' | 'error' | string | null | undefined,
): { status?: 'success' | 'warning' | 'error'; text: string } => {
  switch (indexerStatus) {
    case 'active':
      return { status: 'warning', text: 'Procesando' }
    case 'idle':
      return { status: 'success', text: 'Disponible' }
    case 'error':
      return { status: 'error', text: 'Error' }
    default:
      return { text: 'Desconocido' }
  }
}

type SpaceDetailPageProps = {
  knowledgeBaseId: string
  spaceId: string
  hasSpacesEnabled: boolean
  currentSpaceFolder?: string
  knowledgeBase: KnowledgeBaseModel
}

const SpaceDetailPage = observer(
  ({
    knowledgeBaseId,
    spaceId,
    hasSpacesEnabled,
    currentSpaceFolder,
    knowledgeBase,
  }: SpaceDetailPageProps) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { spaces, knowledgeBases, auth } = rootStore

    const originKnowledgeBaseId = (
      location.state as { originKnowledgeBaseId?: string }
    )?.originKnowledgeBaseId

    // Check if user is viewing global KB with read-only permissions
    const isGlobalKB = knowledgeBaseId === 'global'
    const isReadOnlyGlobalKB = isGlobalKB && auth.user?.role !== 'admin'

    const [globalKBSize, setGlobalKBSize] = useState(0)

    useEffect(() => {
      // Fetch global KB size to include in storage calculations
      knowledgeBases
        .fetchById(MASKED_GLOBAL_TOOLKIT_ID)
        .then((info: KnowledgeBaseModel) => {
          setGlobalKBSize(info.total_documents_size)
        })
        .catch(() => {
          // Silently fail if global KB not accessible
          setGlobalKBSize(0)
        })

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const maxKBStorage = useMemo(() => {
      const realSize = knowledgeBase?.max_size
        ? bytesToGB(knowledgeBase.max_size)
        : 0

      return Math.max(realSize, TOTAL_KB_STORAGE)
    }, [knowledgeBase?.max_size])

    const totalUsedSize = useMemo(() => {
      const orgSize = knowledgeBase?.total_documents_size || 0
      return orgSize + globalKBSize
    }, [knowledgeBase?.total_documents_size, globalKBSize])

    const supportsFileUploads = !!knowledgeBase?.support_file_upload

    const supportsDocumentIndexStatus =
      knowledgeBase?.vector_storage_provider_config?.provider === 'ragflow'

    const {
      isLoadingInitialDocs,
      // count,
      documents,

      displayedDocuments,

      // TODO: Add search query
      // searchQueryValue,
      // setSearchQuery,

      hasPreviousPage,
      hasNextPage,

      fetchPreviousPage,
      refreshPage,
      fetchNextPage,
    } = useKnowledgeBaseDocuments(
      knowledgeBaseId!,
      TOTAL_DOCUMENTS_PER_PAGE,
      hasSpacesEnabled ? currentSpaceFolder : undefined,
    )

    const paginationOptions = useMemo(() => {
      return {
        displayedDocuments,

        hasPreviousPage,
        hasNextPage,

        onPreviousPage: fetchPreviousPage,
        onNextPage: fetchNextPage,
      }
    }, [
      hasPreviousPage,
      hasNextPage,
      fetchPreviousPage,
      fetchNextPage,
      displayedDocuments,
    ])

    const unprefixedDocuments = hasSpacesEnabled
      ? documents
          .filter((doc) => {
            const folderPrefix = currentSpaceFolder
              ? `${currentSpaceFolder}/`
              : ''
            return doc.display_path.startsWith(folderPrefix)
          })
          .map((doc) => {
            // Create a copy with modified display path to remove space prefix
            const folderPrefix = currentSpaceFolder
              ? `${currentSpaceFolder}/`
              : ''
            if (folderPrefix && doc.display_path.startsWith(folderPrefix)) {
              return {
                ...doc,
                display_path: doc.display_path.substring(folderPrefix.length),
              }
            }
            return doc
          })
      : documents

    const { filteredData: filteredDocuments, handleSearch } = useTableSearch(
      unprefixedDocuments,
      ['display_path'],
    )
    const {
      sortedData: sortedDocuments,
      sortConfig,
      handleSort,
    } = useTableSort(filteredDocuments)

    const displayName =
      spaceId === 'public'
        ? 'Espacio público'
        : spaces.selectedSpace?.displayName || 'Espacio sin nombre'

    const handleRemoveDocument = async (documentId: string) => {
      if (!supportsFileUploads) {
        return
      }

      const deletingDoc = knowledgeBases.documentsBeingRemoved[documentId]

      if (deletingDoc) {
        return
      }

      await knowledgeBases.removeDocument(knowledgeBaseId!, documentId)
      refreshPage()
    }

    const handleDownloadDocument = async (
      documentId: string,
      displayPath: string,
    ) => {
      // If we're in a space, we need to add the space folder prefix back to the display path
      const fullDisplayPath =
        hasSpacesEnabled && currentSpaceFolder
          ? `${currentSpaceFolder}/${displayPath}`
          : displayPath

      await knowledgeBases.downloadDocument(
        knowledgeBaseId!,
        documentId,
        fullDisplayPath,
      )
    }

    // Handle file upload with the current space as base path
    const handleFileUpload = async (files: File[] | null): Promise<void> => {
      if (!supportsFileUploads || !Array.isArray(files)) {
        return
      }

      for (const file of files) {
        if (hasSpacesEnabled && currentSpaceFolder) {
          await knowledgeBases.uploadFile(
            knowledgeBaseId!,
            file,
            currentSpaceFolder,
          )
        } else {
          await knowledgeBases.uploadFile(knowledgeBaseId!, file)
        }
      }

      await refreshPage(true)
    }

    if (isLoadingInitialDocs) {
      return (
        <div className="top-loading-bar">
          <LinearProgress />
        </div>
      )
    }

    return (
      <TransitionLayout key={currentSpaceFolder}>
        <div className="spaceDetail-page">
          <button
            className="spaceDetail-page-closeButton"
            onClick={() =>
              navigate(
                `/knowledge-bases/${originKnowledgeBaseId || knowledgeBaseId}`,
              )
            }
          >
            Cerrar espacio
            <span className="spaceDetail-page-closeButton-icon">
              <Icon size={16} name={Icons.Cross} />
            </span>
          </button>
          <Heading variant="system" title={displayName} size="large" />

          {isReadOnlyGlobalKB && (
            <div className="spaceDetail-page-block">
              <p style={{ opacity: 0.7, fontStyle: 'italic' }}>
                <Icon size={16} name={Icons.Info} /> Selección personalizada de
                documentos - Solo Lectura
              </p>
            </div>
          )}

          {supportsFileUploads && !isReadOnlyGlobalKB && (
            <div className="spaceDetail-page-block">
              <FileUploader
                mode="uploader"
                variant="knowledge-base"
                onFilesUpload={handleFileUpload}
                labelIdle="Arrastra y suelta tus archivos aquí."
                buttonProps={{
                  text: 'Elegir archivos',
                  iconName: Icons.Upload,
                }}
              />
            </div>
          )}

          <div className="spaceDetail-page-block">
            {unprefixedDocuments.length > 9 && (
              <TableSearch
                onSearch={handleSearch}
                placeholder="Introduce el nombre de la persona que quieres buscar"
              />
            )}
            <Table
              paginationOptions={paginationOptions}
              className="layout-fixed"
            >
              <colgroup>
                <col />
                <col />
                <col className="table-col-shrink" />
              </colgroup>
              <TableHeader>
                <TableHeaderCell
                  column="name"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                >
                  Nombre
                </TableHeaderCell>
                {supportsDocumentIndexStatus && (
                  <TableHeaderCell
                    column="name"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  >
                    Estado
                  </TableHeaderCell>
                )}
                <TableHeaderCell
                  column="lastModification"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                >
                  Última modificación
                </TableHeaderCell>
                <TableHeaderCell align="center">Acciones</TableHeaderCell>
              </TableHeader>
              <TableBody>
                {sortedDocuments.length > 0 ? (
                  sortedDocuments.map((item) => {
                    const isBeingDownloaded =
                      knowledgeBases.documentsBeingDownloaded[item.path_or_id]

                    const isBeingRemoved =
                      knowledgeBases.documentsBeingRemoved[item.path_or_id]

                    const { status, text } = getDocumentStatusProps(
                      item.indexer_status,
                    )

                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <span title={item.display_path}>
                            {item.display_path}
                          </span>
                        </TableCell>
                        {supportsDocumentIndexStatus && (
                          <TableCell>
                            <Chip status={status} text={text} />
                          </TableCell>
                        )}
                        <TableCell>{toDisplayDate(item.updated_at)}</TableCell>
                        <TableCellActions gap="small" align="center">
                          <TableCellButton
                            onClick={() =>
                              handleDownloadDocument(
                                item.path_or_id,
                                item.display_path,
                              )
                            }
                            isLoading={isBeingDownloaded}
                            isDisabled={isBeingDownloaded || isBeingRemoved}
                          >
                            <Icon size={14} name={Icons.Download} />
                            <span className="sr-only">Descargar</span>
                          </TableCellButton>

                          {supportsFileUploads && !isReadOnlyGlobalKB && (
                            <TableCellButton
                              isLoading={isBeingRemoved}
                              isDisabled={isBeingDownloaded || isBeingRemoved}
                              onClick={() =>
                                handleRemoveDocument(item.path_or_id)
                              }
                            >
                              <Icon size={14} name={Icons.Trash} />
                              <span className="sr-only">Eliminar</span>
                            </TableCellButton>
                          )}
                        </TableCellActions>
                      </TableRow>
                    )
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={3}>
                      No hay datos que coincidan con esa búsqueda
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="spaceDetail-page-block">
            <Progress
              label="Capacidad total de la base de conocimiento"
              description={`Total almacenamiento ${maxKBStorage}GB (${((bytesToGB(totalUsedSize) / maxKBStorage) * 100).toFixed(1)}%)`}
              value={bytesToGB(totalUsedSize)}
              max={maxKBStorage}
            />
          </div>
        </div>
      </TransitionLayout>
    )
  },
)

// HACK! Separate fetcher from view to only render view when data is ready.
const SpaceDetailFetcher = observer(() => {
  const { id: knowledgeBaseId, spaceId } = useParams()

  const { organizations, spaces, departments, auth } = rootStore

  const canAccessRAG = checkRAGAccess(
    knowledgeBaseId,
    organizations.planInfo,
    auth.user!,
  )

  const isGlobalKB = knowledgeBaseId === 'global'

  // Admins accessing global KB should have RAG enabled by default
  const isAdminAccessingGlobalKB = isGlobalKB && auth.user?.role === 'admin'

  const hasRAGEnabled = Boolean(
    isAdminAccessingGlobalKB ||
      (canAccessRAG && organizations.planInfo?.hasRAGEnabled),
  )

  const { knowledgeBase, isLoading: isLoadingKnowledgeBase } =
    useKnowledgeBaseById(
      hasRAGEnabled ? knowledgeBaseId : undefined,
      // Disable polling for global KB to avoid unnecessary requests
      isGlobalKB ? false : hasRAGEnabled,
    )

  const hasSpacesEnabled = isGlobalKB
    ? // HACK! Managed KBs are the only ones that support file uploads, hence spaces are enabled.
      knowledgeBase?.support_file_upload
    : Boolean(hasRAGEnabled && organizations.planInfo?.enableSpaces)

  useEffect(() => {
    if (spaceId === 'public') {
      spaces.setSelectedSpaceId(spaceId)
    } else if (hasSpacesEnabled) {
      spaces.getSpaceById(spaceId!).then(() => {
        spaces.setSelectedSpaceId(spaceId!)
      })
    }

    if (!departments.initiallyLoaded) {
      departments.fetchDepartments()
    }

    return () => {
      spaces.setSelectedSpaceId(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSpacesEnabled, spaceId])

  if (!hasRAGEnabled) {
    return <CantAccessKnowledgeBase />
  }

  const isLoadingSpace =
    spaceId === 'public'
      ? false
      : spaces.isSpaceFetching(spaceId!) ||
        spaces.selectedSpace?.id !== spaceId ||
        !spaces.selectedSpace?.folderName

  if (
    isLoadingKnowledgeBase ||
    !knowledgeBase ||
    isLoadingSpace ||
    !departments.initiallyLoaded
  ) {
    return (
      <div className="top-loading-bar">
        <LinearProgress />
      </div>
    )
  }

  return (
    <SpaceDetailPage
      knowledgeBaseId={knowledgeBaseId!}
      spaceId={spaceId!}
      hasSpacesEnabled={!!hasSpacesEnabled}
      currentSpaceFolder={spaces.selectedSpace?.folderName}
      knowledgeBase={knowledgeBase}
    />
  )
})

export default SpaceDetailFetcher
